import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { normalizeMarkdownContent } from "~/features/projectTop/pageView/utils/markdownParser";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

interface RowData {
  その他機能: string;
  内訳: string;
  内容: string;
  注記: string;
}

interface NonFunctionalRequirementState {
  data: RowData[];
  copyData: RowData[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  documentInfo: {
    createdAt: string;
    modifiedAt: string;
    modifiedBy?: {
      display_name: string;
    };
  } | null;
  isMarkdown: boolean;
  markdownContent: string;
  initialMarkdownContent: string;
}

const initialState: NonFunctionalRequirementState = {
  data: [],
  copyData: [],
  isEditable: false,
  isLoading: true,
  error: null,
  documentInfo: null,
  isMarkdown: false,
  markdownContent: "",
  initialMarkdownContent: "",
};

// APIインスタンスの作成
const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

// 非機能要件データの取得
export const fetchNonFunctionalRequirement = createAsyncThunk(
  "nonFunctionalRequirement/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const documentId =
        documentIdArg || currentProject.outputs?.["non-functional-requirements"]?.id;

      if (!documentId) {
        return rejectWithValue("非機能要件書が見つかりません");
      }

      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );

      if (response === null) {
        return rejectWithValue("データの取得に失敗しました");
      }

      const rawNonFunctionalRequirement = response.content ?? "";
      let nonFunctionalRequirementRows: RowData[] = [];
      let isMarkdown = false;
      let markdownContent = "";

      try {
        const parsedNonFunctionalRequirement: { rows?: unknown[] } = JSON.parse(
          rawNonFunctionalRequirement,
        );
        if (Array.isArray(parsedNonFunctionalRequirement?.rows)) {
          nonFunctionalRequirementRows = parsedNonFunctionalRequirement.rows.map((row: unknown) => {
            const r = row as string[];
            return {
              その他機能: r[0] ?? "",
              内訳: r[1] ?? "",
              内容: r[2] ?? "",
              注記: r[3] ?? "",
            };
          });
        } else {
          isMarkdown = true;
          markdownContent = normalizeMarkdownContent(rawNonFunctionalRequirement);
        }
      } catch (_error) {
        isMarkdown = true;
        markdownContent = normalizeMarkdownContent(rawNonFunctionalRequirement);
      }

      return {
        data: nonFunctionalRequirementRows,
        documentInfo: {
          createdAt: response.created_at || "",
          modifiedAt: response.modified_at || "",
          modifiedBy: response.modified_by,
        },
        isMarkdown,
        markdownContent,
      };
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

// ロックの取得
export const acquireLock = createAsyncThunk(
  "nonFunctionalRequirement/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["non-functional-requirements"]?.id) {
        return rejectWithValue("非機能要件書が見つかりません");
      }

      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "non-functional-requirements",
        mode: "edit",
      });

      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// ロックの解放
export const releaseLock = createAsyncThunk(
  "nonFunctionalRequirement/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["non-functional-requirements"]?.id) {
        return rejectWithValue("非機能要件書が見つかりません");
      }

      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "non-functional-requirements",
        mode: "edit",
      });

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解放中にエラーが発生しました");
    }
  },
);

// データの更新
export const updateNonFunctionalRequirement = createAsyncThunk(
  "nonFunctionalRequirement/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { data, isMarkdown, markdownContent } = state.nonFunctionalRequirement;

      if (!currentProject.outputs?.["non-functional-requirements"]?.id) {
        return rejectWithValue("非機能要件書が見つかりません");
      }

      if (isMarkdown) {
        const response = await documentApi.updateContent(
          currentUser.organization_id,
          currentProject.id,
          currentProject.outputs?.["non-functional-requirements"]?.id,
          { content: markdownContent },
        );

        if (response === null) {
          return rejectWithValue("データの更新に失敗しました");
        }

        await dispatch(releaseLock());

        return true;
      }

      const headers = ["その他機能", "内訳", "内容", "注記"];
      const rows = data.map((row) => headers.map((header) => row[header as keyof RowData] || ""));

      const updateData = {
        content: JSON.stringify({
          headers,
          rows,
        }),
      };

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.["non-functional-requirements"]?.id,
        updateData,
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

export const updateNonFunctionalRequirementMarkdownContent = createAsyncThunk(
  "nonFunctionalRequirement/updateMarkdownContent",
  async (content: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["non-functional-requirements"]?.id) {
        return rejectWithValue("非機能要件書が見つかりません");
      }

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.["non-functional-requirements"]?.id,
        { content },
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

const nonFunctionalRequirementSlice = createSlice({
  name: "nonFunctionalRequirement",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<RowData[]>) => {
      state.data = action.payload;
      state.isMarkdown = false;
      state.markdownContent = "";
      state.initialMarkdownContent = "";
    },
    setCopyData: (state, action: PayloadAction<RowData[]>) => {
      state.copyData = action.payload;
      state.initialMarkdownContent = state.markdownContent;
    },
    restoreFromCopyData: (state) => {
      state.data = JSON.parse(JSON.stringify(state.copyData));
      state.markdownContent = state.initialMarkdownContent;
    },
    updateRow: (state, action: PayloadAction<{ index: number; data: RowData }>) => {
      state.data[action.payload.index] = action.payload.data;
    },
    addRow: (state, action: PayloadAction<RowData>) => {
      state.data.push(action.payload);
    },
    deleteRow: (state, action: PayloadAction<number>) => {
      state.data = state.data.filter((_, index) => index !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    updateMarkdownContent: (state, action: PayloadAction<string>) => {
      state.markdownContent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNonFunctionalRequirement
      .addCase(fetchNonFunctionalRequirement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNonFunctionalRequirement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isMarkdown = action.payload.isMarkdown;
        state.markdownContent = action.payload.markdownContent;
        state.initialMarkdownContent = action.payload.markdownContent;
        if (action.payload.isMarkdown) {
          state.data = [];
          state.copyData = [];
        } else {
          state.data = action.payload.data;
          state.copyData = JSON.parse(JSON.stringify(action.payload.data));
        }
        if (action.payload.documentInfo.modifiedBy) {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
            modifiedBy: {
              display_name: action.payload.documentInfo.modifiedBy.display_name,
            },
          };
        } else {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
          };
        }
      })
      .addCase(fetchNonFunctionalRequirement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // acquireLock
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data)); // 編集開始時にデータをコピー
        state.initialMarkdownContent = state.markdownContent;
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // releaseLock
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updateNonFunctionalRequirement
      .addCase(updateNonFunctionalRequirement.fulfilled, (state) => {
        state.isEditable = false;
        if (state.isMarkdown) {
          state.initialMarkdownContent = state.markdownContent;
        } else {
          state.copyData = JSON.parse(JSON.stringify(state.data));
        }
      })
      .addCase(updateNonFunctionalRequirement.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateNonFunctionalRequirementMarkdownContent.fulfilled, (_state) => {
        // isEditable は releaseLock.fulfilled で false になる
      })
      .addCase(updateNonFunctionalRequirementMarkdownContent.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  setCopyData,
  restoreFromCopyData,
  updateRow,
  addRow,
  deleteRow,
  clearError,
  updateMarkdownContent,
} = nonFunctionalRequirementSlice.actions;

export default nonFunctionalRequirementSlice.reducer;
